import type {ModuleTopic} from '@cp/model';
import {ContentContainer} from '@coolcolduk/ui-mui';
import {ArrowBack} from '@mui/icons-material';
import {Box, Button, Typography} from '@mui/material';
import {useState} from 'react';
import {useNavigate, useParams} from 'react-router';
import {QuestionStatus} from '@cp/model';
import BulkChangeQuestionStatusDialog from '../../../components/BulkChangeQuestionStatusDialog/BulkChangeQuestionStatusDialog';
import ErrorComponent from '../../../components/ErrorComponent/ErrorComponent';
import QuestionStatusCounts from '../../../components/QuestionStatusCounts/QuestionStatusCounts';
import {PrivateRouteProps} from '../../../config/routeType';
import {useCountQuestionsByStatus} from '../../../hooks/question/useCountQuestionsByStatus';
import {useCreateBulkChangeQuestionStatusJob} from '../../../hooks/useCreateBulkChangeQuestionStatusJob';
import {useCreateQuestionGenerationJob} from '../../../hooks/jobs/useCreateQuestionGenerationJob';
import {useCreateQuestionValidationGptJob} from '../../../hooks/jobs/useCreateQuestionValidationGptJob';
import {useCreateQuestionValidationJob} from '../../../hooks/jobs/useCreateQuestionValidationJob';
import {useCreateSeedQuestionVariantJob} from '../../../hooks/jobs/useCreateSeedQuestionVariantJob';
import {useCreateStudyGenerationJob} from '../../../hooks/jobs/useCreateStudyGenerationJob';
import useGetExamModule from '../../../hooks/examModules/useGetExamModule';
import {useShuffleQuestions} from '../../../hooks/jobs/useShuffleQuestions';
import LoadingPage from '../../other/LoadingPage';
import GenerateSeedQuestionVariantDialog from './GenerateSeedQuestionVariantDialog';
import ModuleViewInfo from './ModuleViewInfo';
import ModuleViewTopicsHeader from './ModuleViewTopicsHeader';
import ModuleViewTopicsTable from './ModuleViewTopicsTable';

export default function AdminModuleViewPage(_props: PrivateRouteProps) {
  const {id} = useParams<{id: string}>();
  const navigate = useNavigate();
  const {examModule, loading, error} = useGetExamModule(id ?? '', {skip: !id});

  const {createQuestionGenerationJob, loading: generateAllLoading} = useCreateQuestionGenerationJob();
  const {shuffleQuestions, loading: shuffleLoading} = useShuffleQuestions();
  const {createQuestionValidationJob, loading: validateLoading} = useCreateQuestionValidationJob();
  const {createQuestionValidationGptJob, loading: validateGptLoading} = useCreateQuestionValidationGptJob();
  const {createStudyGenerationJob, loading: generateStudiesLoading} = useCreateStudyGenerationJob();
  const {createBulkChangeQuestionStatusJob, loading: bulkChangeLoading} = useCreateBulkChangeQuestionStatusJob();
  const {createSeedQuestionVariantJob, loading: generateWithSeedLoading} = useCreateSeedQuestionVariantJob();
  const {fetchCountsByStatus, questionCounts, loading: countLoading} = useCountQuestionsByStatus();

  const [openBulkChangeDialog, setOpenBulkChangeDialog] = useState(false);
  const [openGenerateSeedQuestionVariantDialog, setOpenGenerateSeedQuestionVariantDialog] = useState(false);

  if (loading) {
    return <LoadingPage />;
  }

  if (error || !examModule) {
    return <ErrorComponent message={error?.message || 'Failed to load module'} />;
  }

  const handleGenerateAllTopics = async () => {
    try {
      const allTopicNames = examModule.topics.map((topic: ModuleTopic) => topic.topic);
      await createQuestionGenerationJob(examModule.id, allTopicNames); // No difficulty specified = all difficulties
    } catch (err) {
      console.error('Error generating questions for all topics:', err);
    }
  };

  const handleShuffleQuestions = async () => {
    try {
      // Get all topics from the module
      const allTopics = examModule.topics.map((topic: ModuleTopic) => topic.topic);
      // Shuffle all topics and all difficulties (1-5)
      await shuffleQuestions(examModule.id, allTopics, [1, 2, 3, 4, 5]);
    } catch (err) {
      console.error('Error creating shuffle job:', err);
    }
  };

  const handleValidateAllQuestions = async () => {
    try {
      // Get all topics from the module
      const allTopics = examModule.topics.map((topic: ModuleTopic) => topic.topic);
      // Validate all topics and all difficulties (1-5)
      await createQuestionValidationJob(examModule.id, allTopics, [1, 2, 3, 4, 5]);
    } catch (err) {
      console.error('Error creating validation job:', err);
    }
  };

  const handleValidateAllQuestionsGpt = async () => {
    try {
      // Get all topics from the module
      const allTopics = examModule.topics.map((topic: ModuleTopic) => topic.topic);
      // Validate all topics and all difficulties (1-5) using GPT
      await createQuestionValidationGptJob(examModule.id, allTopics, [1, 2, 3, 4, 5]);
    } catch (err) {
      console.error('Error creating GPT validation job:', err);
    }
  };

  const handleCountQuestions = async () => {
    try {
      await fetchCountsByStatus({
        moduleId: examModule.id,
      });
    } catch (err) {
      console.error('Error counting questions:', err);
    }
  };

  const handleGenerateAllStudies = async () => {
    try {
      await createStudyGenerationJob(examModule.id);
    } catch (err) {
      console.error('Error generating studies:', err);
    }
  };

  const handleOpenBulkChangeDialog = () => {
    setOpenBulkChangeDialog(true);
  };

  const handleCloseBulkChangeDialog = () => {
    setOpenBulkChangeDialog(false);
  };

  const handleBulkChangeSubmit = async (filterStatus: QuestionStatus, changeStatus: QuestionStatus) => {
    try {
      const job = await createBulkChangeQuestionStatusJob(examModule.id, filterStatus, changeStatus);
      if (job) {
        setOpenBulkChangeDialog(false);
      }
    } catch (err) {
      console.error('Error creating bulk change status job:', err);
    }
  };

  const handleOpenGenerateWithSeedDialog = () => {
    setOpenGenerateSeedQuestionVariantDialog(true);
  };

  const handleCloseGenerateSeedQuestionVariantDialog = () => {
    setOpenGenerateSeedQuestionVariantDialog(false);
  };

  const handleSubmitGenerateSeedQuestionVariant = async (seedQuestionCount: number, variantsPerSeed: number) => {
    try {
      const job = await createSeedQuestionVariantJob(examModule.id, seedQuestionCount, variantsPerSeed);
      if (job) {
        setOpenGenerateSeedQuestionVariantDialog(false);
      }
    } catch (err) {
      console.error('Error creating seed question variant job:', err);
    }
  };

  return (
    <ContentContainer data-testid="module-view-page">
      <Box sx={{display: 'flex', alignItems: 'center', gap: 2, mb: 3}}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/admin/module')}
          variant="outlined"
          sx={{textTransform: 'none'}}
        >
          Back to Modules
        </Button>
        <Typography variant="h5" sx={{color: '#1B263B', fontWeight: 600}}>
          {examModule.name}
        </Typography>
      </Box>

      <ModuleViewInfo examModule={examModule} />

      {questionCounts && <QuestionStatusCounts questionCounts={questionCounts} />}

      <Box sx={{mt: 2}}>
        <ModuleViewTopicsHeader
          onShuffleQuestions={handleShuffleQuestions}
          onValidateAllQuestions={handleValidateAllQuestions}
          onValidateAllQuestionsGpt={handleValidateAllQuestionsGpt}
          onGenerateAllTopics={handleGenerateAllTopics}
          onGenerateWithSeedQuestion={handleOpenGenerateWithSeedDialog}
          onGenerateAllStudies={handleGenerateAllStudies}
          onCountQuestions={handleCountQuestions}
          onBulkChangeStatus={handleOpenBulkChangeDialog}
          shuffleLoading={shuffleLoading}
          validateLoading={validateLoading}
          validateGptLoading={validateGptLoading}
          generateAllLoading={generateAllLoading}
          generateWithSeedLoading={generateWithSeedLoading}
          generateStudiesLoading={generateStudiesLoading}
          countLoading={countLoading}
          bulkChangeLoading={bulkChangeLoading}
        />
        <ModuleViewTopicsTable examModule={examModule} />
      </Box>

      <BulkChangeQuestionStatusDialog
        open={openBulkChangeDialog}
        onClose={handleCloseBulkChangeDialog}
        onSubmit={handleBulkChangeSubmit}
        loading={bulkChangeLoading}
      />

      <GenerateSeedQuestionVariantDialog
        open={openGenerateSeedQuestionVariantDialog}
        onClose={handleCloseGenerateSeedQuestionVariantDialog}
        onSubmit={handleSubmitGenerateSeedQuestionVariant}
        loading={generateWithSeedLoading}
      />
    </ContentContainer>
  );
}
